(() => {
  if (customElements.get('ahvte-mobile-cart')) return;
  const ROOT = '[data-ahvte-product]';
  const OVERLAY = 'dialog[open], cart-drawer.active, cart-notification.active, #cart-notification.active, [data-ahvte-header].has-open-menu, details[open] > .modal__content';
  const text = (node, value) => {
    if (node.textContent !== value) node.textContent = value;
  };
  customElements.define('ahvte-mobile-cart', class extends HTMLElement {
    connectedCallback() {
      if (this.life) return;
      this.life = new AbortController();
      this.mobile = matchMedia('(max-width: 749px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      this.addButton = this.querySelector('[data-sticky-add]');
      this.variantText = this.querySelector('[data-sticky-variant]');
      this.priceText = this.querySelector('[data-sticky-price]');
      if (!this.addButton || !this.variantText || !this.priceText) return;
      this.spacer = document.createElement('div');
      this.spacer.className = 'ahvte-mobile-cart-spacer';
      this.spacer.setAttribute('aria-hidden', 'true');
      document.body.append(this.spacer);
      const on = (node, name, handler, extra = {}) => node?.addEventListener(name, handler, { signal: this.life.signal, ...extra });
      const schedule = () => this.schedule();
      on(this.addButton, 'click', () => this.activate());
      on(window, 'scroll', schedule, { passive: true });
      on(window, 'resize', schedule, { passive: true });
      on(window.visualViewport, 'resize', schedule, { passive: true });
      on(window.visualViewport, 'scroll', schedule, { passive: true });
      on(this.mobile, 'change', schedule);
      on(this.reducedMotion, 'change', () => {
        if (this.reducedMotion.matches && !this.classList.contains('is-visible')) this.finishHide();
        this.schedule();
      });
      on(window, 'pageshow', () => { this.pending = false; this.needsReview = false; this.sawBusy = false; this.bind(); });
      on(document, 'shopify:section:load', () => this.bind());
      on(document, 'shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.disconnectedCallback();
        else if (event.target.contains(this.root)) { this.unbind(); this.hide(); }
      });
      on(document, 'focusin', schedule);
      on(document, 'focusout', schedule);
      this.pageObserver = new MutationObserver((records) => {
        const relevant = records.some((record) => {
          const target = record.target;
          if (target === this || this.contains(target) || target === this.spacer) return false;
          return target === document.body || target === document.documentElement ||
            target.matches?.('dialog, cart-drawer, cart-notification, #cart-notification, [data-ahvte-header], details, .modal__content') ||
            [...record.addedNodes, ...record.removedNodes].some((node) => node.matches?.('dialog, cart-drawer, cart-notification, ' + ROOT) || node.querySelector?.('dialog, ' + ROOT));
        });
        if (!relevant) return;
        if (!this.root?.isConnected || !this.original?.isConnected) this.bind();
        this.schedule();
      });
      this.pageObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'open', 'hidden', 'aria-hidden'] });
      this.resizeObserver = new ResizeObserver(schedule);
      this.resizeObserver.observe(this);
      this.bind();
    }
    disconnectedCallback() {
      this.life?.abort();
      this.life = null;
      this.unbind();
      this.pageObserver?.disconnect();
      this.resizeObserver?.disconnect();
      clearTimeout(this.hideTimer);
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.spacer?.remove();
      this.pending = false;
      this.needsReview = false;
      this.sawBusy = false;
      this.hidden = true;
      this.classList.remove('is-visible');
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
    }
    unbind() {
      this.binding?.abort();
      this.binding = null;
      this.rootObserver?.disconnect();
      this.intersection?.disconnect();
      if (this.root) this.resizeObserver?.unobserve(this.root);
      this.root = null;
      this.form = null;
      this.original = null;
    }
    bind() {
      if (!this.life || !this.isConnected) return;
      const candidates = [...document.querySelectorAll(ROOT)].filter((node) => node.dataset.productId === this.dataset.productId);
      if (candidates.length !== 1 || document.querySelector('ahvte-mobile-cart') !== this) {
        this.unbind(); this.hide(); return;
      }
      const root = candidates[0];
      const form = root.querySelector('form.ahvte-product__form');
      const original = form?.querySelector('[data-add-to-cart]');
      if (!form || !original || original.form !== form) { this.unbind(); this.hide(); return; }
      if (this.root === root && this.form === form && this.original === original) { this.schedule(); return; }
      this.unbind();
      this.root = root;
      this.form = form;
      this.original = original;
      this.pending = false;
      this.needsReview = false;
      this.sawBusy = false;
      this.binding = new AbortController();
      const signal = this.binding.signal;
      for (const event of ['change', 'input', 'ahvte:variant-change']) root.addEventListener(event, () => this.schedule(), { signal });
      form.addEventListener('invalid', (event) => {
        this.pending = false;
        this.invalidAttempt = true;
        event.target.scrollIntoView({ block: 'center', behavior: 'auto' });
        this.schedule();
      }, { capture: true, signal });
      form.addEventListener('submit', (event) => {
        this.pending = true;
        this.needsReview = false;
        this.addButton.disabled = true;
        queueMicrotask(() => {
          if (signal.aborted) return;
          if (event.defaultPrevented && !this.readState()?.busy) {
            this.pending = false;
            this.needsReview = true;
          }
          this.schedule();
        });
        this.schedule();
      }, { capture: true, signal });
      this.rootObserver = new MutationObserver(() => this.schedule());
      this.rootObserver.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'class', 'value', 'checked'] });
      this.intersection = new IntersectionObserver(() => this.schedule(), { threshold: [0, 1] });
      this.intersection.observe(original);
      this.resizeObserver.observe(root);
      this.schedule();
    }
    schedule() {
      if (this.frame || !this.life) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
    }
    readState() {
      let variants;
      try { variants = JSON.parse(this.root.querySelector('[data-product-variants]').textContent); }
      catch { return null; }
      if (!Array.isArray(variants)) return null;
      const id = this.form.querySelector('[name="id"]')?.value;
      const groups = [...this.root.querySelectorAll('[data-option-group]')];
      const options = groups.map((group) => group.querySelector('[data-option-input]:checked')?.value);
      const variant = variants.find((candidate) => String(candidate.id) === id);
      const selectionMatches = Boolean(variant && (!groups.length ||
        (options.length === variant.options.length && options.every((value, i) => value !== undefined && value === variant.options[i]))));
      const busy = this.original.getAttribute('aria-busy') === 'true' || this.form.getAttribute('aria-busy') === 'true' || this.original.classList.contains('loading') || this.original.classList.contains('is-loading');
      return {
        variant: selectionMatches ? variant : null,
        title: groups.length ? options.filter((value) => value !== undefined).join(' / ') : (this.dataset.defaultOnly === 'true' ? '' : variant?.title || ''),
        price: selectionMatches ? this.root.querySelector('[data-product-price]')?.textContent.trim() || '' : '',
        busy,
        disabled: !selectionMatches || !variant.available || this.original.disabled || this.original.getAttribute('aria-disabled') === 'true',
        label: !selectionMatches ? this.dataset.unavailable : !variant.available ? this.dataset.soldOut : this.original.querySelector('[data-add-label]')?.textContent.trim() || this.dataset.addLabel
      };
    }
    sync() {
      if (!this.root?.isConnected || !this.original?.isConnected) { this.bind(); this.hide(); return; }
      const state = this.readState();
      if (!state) { this.hide(); return; }
      if (state.busy) this.sawBusy = true;
      if (this.sawBusy && !state.busy) { this.pending = false; this.needsReview = false; this.sawBusy = false; }
      text(this.variantText, state.title);
      text(this.priceText, state.price);
      text(this.addButton, this.needsReview ? this.dataset.reviewLabel : state.label);
      this.addButton.disabled = state.disabled || state.busy || Boolean(this.pending);
      this.addButton.setAttribute('aria-busy', String(state.busy || Boolean(this.pending)));
      const rect = this.original.getBoundingClientRect();
      const editing = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, select, [contenteditable="true"]');
      const overlay = document.querySelector(OVERLAY);
      const visible = this.mobile.matches && rect.width > 0 && rect.height > 0 && rect.bottom <= this.visibleTop(rect) && !overlay && !editing;
      if (!visible) { this.hide(); return; }
      this.show();
      this.spacer.style.setProperty('--ahvte-mobile-cart-height', `${Math.ceil(this.getBoundingClientRect().height)}px`);
    }
    visibleTop(buttonRect) {
      let top = window.visualViewport?.offsetTop || 0;
      for (const header of document.querySelectorAll('[data-ahvte-header] > .ahvte-header')) {
        const style = getComputedStyle(header);
        const rect = header.getBoundingClientRect();
        if (!['fixed', 'sticky'].includes(style.position) || style.visibility === 'hidden' ||
          style.display === 'none' || Number(style.opacity) === 0) continue;
        if (rect.height > 0 && rect.top <= top + 1 && rect.bottom > top &&
          rect.left <= buttonRect.left && rect.right >= buttonRect.right) top = rect.bottom;
      }
      return top;
    }
    show() {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
      if (this.classList.contains('is-visible')) return;
      this.hidden = false;
      void this.offsetHeight;
      this.inert = false;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-visible');
    }
    finishHide() {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
      if (this.classList.contains('is-visible')) return;
      this.hidden = true;
      this.spacer?.style.setProperty('--ahvte-mobile-cart-height', '0px');
    }
    hide() {
      if (this.contains(document.activeElement)) {
        if (document.querySelector(OVERLAY)) document.activeElement.blur();
        else this.original?.focus({ preventScroll: true });
      }
      const wasVisible = this.classList.contains('is-visible');
      this.classList.remove('is-visible');
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      if (this.hidden || this.reducedMotion?.matches || !this.mobile?.matches) {
        this.finishHide();
      } else if (wasVisible && !this.hideTimer) {
        this.hideTimer = setTimeout(() => this.finishHide(), 220);
      }
    }
    activate() {
      this.sync();
      if (this.hidden || this.inert || !this.classList.contains('is-visible') || this.addButton.disabled || !this.original) return;
      if (this.needsReview) {
        this.form.scrollIntoView({ block: 'center', behavior: 'auto' });
        this.original.focus({ preventScroll: true });
        return;
      }
      this.invalidAttempt = false;
      this.pending = true;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      let submitted = false;
      const mark = () => { submitted = true; };
      this.form.addEventListener('submit', mark, { once: true });
      this.original.click();
      this.form.removeEventListener('submit', mark);
      if (!submitted && !this.readState()?.busy) {
        this.pending = false;
        this.needsReview = !this.invalidAttempt;
      }
      this.schedule();
    }
  });
})();